import { Injectable, inject } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { API_CONFIG, ApiConfig, DEFAULT_API_CONFIG } from '../base/api-config';
import { ENSURE_FRESH_CREDENTIALS } from '../base/http-context.tokens';
import type { Vessel } from './vessel.service';
import { waitForInFlightHubStart, buildSignalrHttpOptions } from './signalr-reconnect.util';

export type VesselChangeType = 'Created' | 'Updated' | 'StatusChanged' | 'Archived' | 'Restored';

export interface VesselChangedDto {
  marinaId: string;
  vessel: Vessel;
  changeType: VesselChangeType | string;
  updatedAt: string;
  updatedByUserId?: string | null;
}

export interface VesselRemovedDto {
  marinaId: string;
  vesselId: string;
}

/**
 * SignalR client for real-time vessel/fleet updates. Connect when opening the vessels page.
 */
@Injectable({
  providedIn: 'root',
})
export class VesselsHubService {
  private connection: HubConnection | null = null;
  /** Single-flight so concurrent connect()/subscribe() callers wait for Connected. */
  private connectPromise: Promise<void> | null = null;
  private readonly config: ApiConfig = inject(API_CONFIG, { optional: true }) ?? DEFAULT_API_CONFIG;
  /** Awaited before connecting — SignalR bypasses the HTTP interceptors entirely. */
  private readonly ensureFreshCredentials = inject(ENSURE_FRESH_CREDENTIALS, { optional: true });
  private subscribedMarinaIds = new Set<string>();
  /** Ref count so sidebar and feature pages can share one hub subscription per marina. */
  private marinaSubscriptionRefCounts = new Map<string, number>();
  private createdHandlers: ((dto: VesselChangedDto) => void)[] = [];
  private updatedHandlers: ((dto: VesselChangedDto) => void)[] = [];
  private removedHandlers: ((dto: VesselRemovedDto) => void)[] = [];

  private get hubUrl(): string {
    const base = this.config.baseUrl.replace(/\/$/, '');
    return `${base}/hubs/vessels`;
  }

  async connect(): Promise<void> {
    if (this.isHubConnected()) return;
    if (this.connectPromise) {
      await this.connectPromise;
      if (this.isHubConnected()) return;
    }

    this.connectPromise = this.startConnection();
    try {
      await this.connectPromise;
    } finally {
      this.connectPromise = null;
    }
  }

  private async startConnection(): Promise<void> {
    if (this.isHubConnected()) return;

    // Refresh first if the access token is spent. SignalR negotiates with its own HTTP client, so
    // nothing else will do this for us, and negotiating with a lapsed cookie means a 401 per hub and
    // a "Connection lost" dialog for what is really just a routine token refresh.
    await this.ensureFreshCredentials?.();

    if (this.connection) {
      const state = this.connection.state;
      if (state === HubConnectionState.Connected || state === HubConnectionState.Connecting) {
        return;
      }
      try {
        await this.connection.stop();
      } catch {
        // ignore teardown errors
      }
      this.connection = null;
    }

    this.connection = new HubConnectionBuilder()
      .withUrl(this.hubUrl, buildSignalrHttpOptions(this.ensureFreshCredentials))
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    this.connection.on('VesselCreated', (dto: VesselChangedDto) => {
      this.createdHandlers.forEach((h) => h(dto));
    });

    this.connection.on('VesselUpdated', (dto: VesselChangedDto) => {
      this.updatedHandlers.forEach((h) => h(dto));
    });

    this.connection.on('VesselRemoved', (dto: VesselRemovedDto) => {
      this.removedHandlers.forEach((h) => h(dto));
    });

    this.connection.onreconnected(async () => {
      for (const marinaId of this.subscribedMarinaIds) {
        try {
          await this.connection?.invoke('SubscribeToVessels', marinaId);
        } catch (e) {
          console.warn('[VesselsHubService] Resubscribe failed for marina', marinaId, e);
        }
      }
    });

    await this.connection.start();
  }

  async disconnect(): Promise<void> {
    const inFlight = this.connectPromise;
    const hub = this.connection;
    await waitForInFlightHubStart(inFlight);
    if (this.connectPromise === inFlight) {
      this.connectPromise = null;
    }
    if (hub) {
      try {
        await hub.stop();
      } catch {
        // ignore teardown errors
      }
      if (this.connection === hub) {
        this.connection = null;
      }
      this.subscribedMarinaIds.clear();
      this.marinaSubscriptionRefCounts.clear();
    }
  }

  onVesselCreated(handler: (dto: VesselChangedDto) => void): () => void {
    this.createdHandlers.push(handler);
    return () => {
      this.createdHandlers = this.createdHandlers.filter((h) => h !== handler);
    };
  }

  onVesselUpdated(handler: (dto: VesselChangedDto) => void): () => void {
    this.updatedHandlers.push(handler);
    return () => {
      this.updatedHandlers = this.updatedHandlers.filter((h) => h !== handler);
    };
  }

  onVesselRemoved(handler: (dto: VesselRemovedDto) => void): () => void {
    this.removedHandlers.push(handler);
    return () => {
      this.removedHandlers = this.removedHandlers.filter((h) => h !== handler);
    };
  }

  async subscribeToVessels(marinaId: string): Promise<void> {
    const prev = this.marinaSubscriptionRefCounts.get(marinaId) ?? 0;
    const next = prev + 1;
    this.marinaSubscriptionRefCounts.set(marinaId, next);
    if (next === 1) {
      try {
        await this.ensureConnected();
        await this.connection!.invoke('SubscribeToVessels', marinaId);
        this.subscribedMarinaIds.add(marinaId);
      } catch (e) {
        this.marinaSubscriptionRefCounts.delete(marinaId);
        throw e;
      }
    }
  }

  async unsubscribeFromVessels(marinaId: string): Promise<void> {
    const prev = this.marinaSubscriptionRefCounts.get(marinaId) ?? 0;
    if (prev === 0) {
      return;
    }
    if (prev === 1) {
      this.marinaSubscriptionRefCounts.delete(marinaId);
      if (this.isHubConnected()) {
        try {
          await this.connection!.invoke('UnsubscribeFromVessels', marinaId);
        } catch (e) {
          console.warn('[VesselsHubService] Unsubscribe failed', marinaId, e);
        }
      }
      this.subscribedMarinaIds.delete(marinaId);
    } else {
      this.marinaSubscriptionRefCounts.set(marinaId, prev - 1);
    }
  }

  private async ensureConnected(): Promise<void> {
    if (this.isHubConnected()) return;
    await this.connect();
    if (!this.isHubConnected()) {
      throw new Error('Vessels SignalR connection is not Connected');
    }
  }

  private isHubConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  get isConnected(): boolean {
    return this.isHubConnected();
  }
}
